'use client'

import { createContext, useContext, useState, useCallback, useEffect } from 'react'

type ModalId = 'call' | 'quiz' | 'chat' | 'form' | null

interface ModalContextType {
  activeModal: ModalId
  openModal: (id: ModalId) => void
  closeModal: () => void
}

const ModalContext = createContext<ModalContextType>({
  activeModal: null,
  openModal: () => {},
  closeModal: () => {},
})

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [activeModal, setActiveModal] = useState<ModalId>(null)

  const openModal = useCallback((id: ModalId) => {
    setActiveModal(id)
    document.body.style.overflow = 'hidden'
  }, [])

  const closeModal = useCallback(() => {
    setActiveModal(null)
    document.body.style.overflow = ''
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeModal])

  return (
    <ModalContext.Provider value={{ activeModal, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  )
}

export function useModal() {
  return useContext(ModalContext)
}
